import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import type { RootState } from '../store/store';
import { setAuthData, clearAuthData } from '../store/slices/authSlice';
import { engageAuthorization } from '../services/loginService';
import Loader from 'src/components/Loader';
import AuthError from 'src/components/AuthError';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ENGAGE_URL = process.env.REACT_APP_ENGAGE_URL ?? '/';

const redirectToEngage = (): void => {
  window.location.href = ENGAGE_URL;
};

// Same redirect target Header's logout uses, so retrying from the auth
// error page ends the session the same way instead of just landing back
// on the Engage dashboard.
const redirectToEngageLogout = (appConfigs: unknown): void => {
  const engageConfig = Array.isArray(appConfigs)
    ? (appConfigs as { configName: string; configValue: string; configType: string }[]).find(
      (config) => config.configName === 'BaseURLFE' && config.configType === 'EngageApp'
    )
    : null;

  let redirectUrl = engageConfig?.configValue;

  if (redirectUrl === undefined || redirectUrl === null || redirectUrl === '') {
    redirectUrl = ENGAGE_URL === '' ? '/' : ENGAGE_URL;
  }

  window.location.href = redirectUrl.endsWith('/') ? redirectUrl + 'logout' : redirectUrl + '/logout';
};

const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = jwtDecode(token);
    return exp == null || Date.now() / 1000 > exp;
  } catch {
    return true;
  }
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { token: reduxToken, appConfigs: reduxAppConfigs } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [authState, setAuthState] = useState<'checking' | 'allowed' | 'redirect' | 'error'>('checking');
  const authAttempted = useRef(false);
  const authCompleted = useRef(false);

  // Proactive token expiry check on every menu navigation.
  // The main useEffect below only runs once (guarded by authCompleted.current),
  // so without this a tampered/expired token would only surface as a 401 in page content.
  useEffect(() => {
    if (authState !== 'allowed') return;
    const currentToken = reduxToken ?? localStorage.getItem('jwtToken');
    if (currentToken === null || currentToken === '' || isTokenExpired(currentToken)) {
      sessionStorage.clear();
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('engagetoken');
      navigate('/logout', { replace: true });
    }
  }, [location.pathname]); // intentionally only fires on route change

  useEffect(() => {
    if (authCompleted.current) return;

    const searchParams = new URLSearchParams(location.search);
    const engageToken = searchParams.get('engageToken') ?? searchParams.get('engagetoken');

    const performAuth = async (token: string): Promise<void> => {
      try {
        const response = await engageAuthorization(token);
        if (response.token === undefined || response.token === null || response.token === '') {
          setAuthState('error');
          return;
        }

        const { user, token: newToken, role, appConfigs, appRights } = response;

        const webhookConfig = Array.isArray(appConfigs)
          ? (appConfigs as unknown as { configName: string }[]).find(
            (c) => c.configName === 'WebhookURLCount'
          )
          : undefined;
        if (webhookConfig !== undefined && webhookConfig !== null) {
          localStorage.setItem('WebhookURLCount', JSON.stringify(webhookConfig));
        }

        sessionStorage.setItem('engageAuthorizationResponse', JSON.stringify(response));
        sessionStorage.setItem('engagetoken', token);
        localStorage.setItem('jwtToken', newToken);
        localStorage.setItem('engagetoken', token);

        dispatch(setAuthData({
          user,
          token: newToken,
          role,
          superadminClientId: String(user.clientId),
          appConfigs,
          appRights,
        }));

        if (isTokenExpired(newToken)) {
          setAuthState('redirect');
          redirectToEngage();
          return;
        }

        authCompleted.current = true;
        navigate('/dashboard', { replace: true });
        setAuthState('allowed');
      } catch {
        // Any failure here — invalid/expired token, wrong endpoint, 5xx,
        // network error — shows a retryable error page instead of silently
        // bouncing to Engage, so it doesn't look like a UI bug.
        setAuthState('error');
      }
    };

    // ─────────────────────────────────────────────
    // PATH A: Fresh engageToken in URL → always re-authenticate.
    // Clears any stale session so a different user's data never
    // leaks through when Engage redirects with a new token.
    // ─────────────────────────────────────────────
    if (engageToken !== null && engageToken !== '') {
      if (authAttempted.current) return;
      authAttempted.current = true;

      sessionStorage.clear();
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('engagetoken');

      void performAuth(engageToken);
      return;
    }

    // ─────────────────────────────────────────────
    // PATH B: No URL token — use existing valid session
    // (page refresh, internal navigation, duplicate tab)
    // ─────────────────────────────────────────────
    const existingSession = sessionStorage.getItem('engageAuthorizationResponse');
    const currentToken = reduxToken ?? localStorage.getItem('jwtToken');

    if ((existingSession !== null && existingSession !== '') || (currentToken !== null && currentToken !== '')) {
      if (currentToken === null || currentToken === '' || isTokenExpired(currentToken)) {
        sessionStorage.clear();
        localStorage.clear();
        setAuthState('redirect');
        redirectToEngage();
        return;
      }

      authCompleted.current = true;
      setAuthState('allowed');
      return;
    }

    // ─────────────────────────────────────────────
    // PATH C: No session and no URL token → back to Engage
    // ─────────────────────────────────────────────
    setAuthState('redirect');
    redirectToEngage();

  }, [reduxToken, navigate, dispatch]);

  const currentToken = reduxToken ?? localStorage.getItem('jwtToken');

  // Retrying with the stale engageToken could loop forever if the token
  // itself was bad, so clear the session and log the user out the same
  // way Header's logout does (redirect to Engage's /logout) instead of
  // just bouncing back to the Engage dashboard.
  const handleRetry = (): void => {
    const storedAppConfig = localStorage.getItem('appConfigs');
    const appConfigs = reduxAppConfigs ?? (storedAppConfig !== null ? (JSON.parse(storedAppConfig) as unknown) : null);

    dispatch(clearAuthData());
    sessionStorage.clear();
    localStorage.clear();
    redirectToEngageLogout(appConfigs);
  };

  if (authState === 'redirect') return null;

  if (authState === 'error') return <AuthError onRetry={handleRetry} />;

  if (authState === 'checking' || (authState === 'allowed' && (currentToken === null || currentToken === ''))) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;